/**
 * Type definitions for Data Adjustments functionality
 * Provides comprehensive type safety for data adjustment workflows
 */

import { UploadProgress as StandardUploadProgress } from '@/shared/components/feedback/ProgressTracker';

// =============================================================================
// WORKFLOW CONFIGURATION
// =============================================================================

/** Workflow steps for the data adjustments flow */
export const WORKFLOW_STEPS = [
  { id: 1, label: 'workflow.steps.upload', description: 'workflow.steps.uploadDesc' },
  { id: 2, label: 'workflow.steps.timeGrid', description: 'workflow.steps.timeGridDesc' },
  { id: 3, label: 'workflow.steps.imputation', description: 'workflow.steps.imputationDesc' },
  { id: 4, label: 'workflow.steps.process', description: 'workflow.steps.processDesc' },
  { id: 5, label: 'workflow.steps.save', description: 'workflow.steps.saveDesc' }
];

// =============================================================================
// CORE TYPES
// =============================================================================

/** Date time state with value and timestamp */
export interface DateTimeState {
  value: string;
  timestamp: number | null;
}

/** Processing method selection options */
export type ProcessingMethod = 'mean' | 'intrpl' | 'nearest' | 'nearest (mean)' | 'nearest (max. delta)' | '';

/** Parameters for data adjustment operations */
export interface AdjustmentsParameters {
  startTime: DateTimeState;
  endTime: DateTimeState;
  timeStepSize: string;
  offset: string;
  selectedMethods: { [key: string]: string };
  intrplMaxValues: { [key: string]: string };
  decimalPlaces: { [key: string]: string };
}

/** Validation errors for parameters */
export interface ParameterValidationErrors {
  startTime?: string;
  endTime?: string;
  timeStepSize?: string;
  offset?: string;
}

/** File metadata information */
export interface FileMetadata {
  name: string;
  size: number;
  lastModified: number;
}

/** Information about processed data file */
export interface InfoType {
  "Name der Datei": string;
  "Name der Messreihe": string;
  "Startzeit (UTC)": string;
  "Endzeit (UTC)": string;
  "Zeitschrittweite [min]": number;
  "Offset [min]": number;
  "Anzahl der Datenpunkte": number;
  "Anzahl der numerischen Datenpunkte": number;
  "Anteil an numerischen Datenpunkten": number;
}

/** Chart data point structure */
export interface DataPoint {
  UTC: string;
  filename?: string;
  [key: string]: string | number | null | undefined;
}

/** File method selection information */
export interface FileMethod {
  filename: string;
  current_timestep: number;
  requested_timestep: number;
  current_offset: number;
  requested_offset: number;
  method?: string;
}

/** Processed data response structure */
export interface ProcessedData {
  success: boolean;
  methodsRequired?: boolean;
  data: {
    info_df: Array<InfoType>;
    upload_id: string;
  };
}

/** Brush range for chart synchronization */
export interface BrushRange {
  startTime: number;
  endTime: number;
}

// =============================================================================
// UI STATE MANAGEMENT
// =============================================================================

/** UI State for the main component */
export interface AdjustmentsUIState {
  currentStep: number;
  completedSteps: number[];
  isNameModalOpen: boolean;
  saveDataName: string;
  errorMessage: string | null;
  errorPersistent: boolean;
  errorCode: string | null;
  errorSuggestion: string | null;
  successMessage: string | null;
  isSaving: boolean;
  isProcessing: boolean;
  showFileUpload: boolean;
  showActionButtons: boolean;
  currentFileIndex: number;
  originalDataName: string;
  originalFileName: string;
}

/** Action types for UI reducer */
export type AdjustmentsUIAction = 
  | { type: 'SET_STEP'; payload: number }
  | { type: 'MARK_STEP_COMPLETED'; payload: number }
  | { type: 'TOGGLE_MODAL'; payload: boolean }
  | { type: 'SET_SAVE_NAME'; payload: string }
  | { type: 'SET_ERROR'; payload: string | null | { message: string; code?: string; suggestion?: string; persistent?: boolean } }
  | { type: 'SET_SUCCESS'; payload: string | null }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'SHOW_FILE_UPLOAD'; payload: boolean }
  | { type: 'SHOW_ACTION_BUTTONS'; payload: boolean }
  | { type: 'SET_FILE_INDEX'; payload: number }
  | { type: 'SET_ORIGINAL_DATA_NAME'; payload: string }
  | { type: 'SET_ORIGINAL_FILE_NAME'; payload: string }
  | { type: 'RESET_UI' };

// =============================================================================
// UPLOAD PROGRESS
// =============================================================================

/** Upload progress type alias for data adjustments */
export type AdjustmentsUploadProgress = StandardUploadProgress;

// =============================================================================
// VALIDATION & DEFAULTS
// =============================================================================

/** Validation rule interface for better type safety */
interface ValidationRule {
  readonly min: number;
  readonly max: number;
  readonly required: boolean;
  readonly fieldName: string;
  readonly unit?: string;
}

/** Validation rules for numeric parameters */
export const VALIDATION_RULES: Readonly<Record<'timeStepSize' | 'offset', ValidationRule>> = {
  timeStepSize: {
    min: 1,
    max: 1440,
    required: true,
    fieldName: 'Zeitschrittweite',
    unit: 'min'
  },
  offset: {
    min: 0,
    max: 1440,
    required: true,
    fieldName: 'Offset',
    unit: 'min'
  }
};

// NOTE: MAX_TOTAL_FILE_SIZE has been moved to useFileSizeLimit hook for dynamic subscription-based limits
// Use: import { useFileSizeLimit } from '@/hooks/useFileSizeLimit'

/** Chunk size for reading large CSV files (50KB) */
export const CSV_CHUNK_SIZE = 50 * 1024;

/** File time range extraction result */
export interface FileTimeRange {
  filename: string;
  minTime: Date | null;
  maxTime: Date | null;
  error?: string;
}

/** File validation result */
export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

/** Default parameter values */
export const DEFAULT_PARAMETERS: Readonly<AdjustmentsParameters> = {
  startTime: { value: '', timestamp: null },
  endTime: { value: '', timestamp: null },
  timeStepSize: '',
  offset: '',
  selectedMethods: {},
  intrplMaxValues: {},
  decimalPlaces: {}
};

/** Processing method options */
export const PROCESSING_METHOD_OPTIONS = [
  { value: 'mean', label: 'methodSelection.methods.mean' },
  { value: 'intrpl', label: 'methodSelection.methods.intrpl' },
  { value: 'nearest', label: 'methodSelection.methods.nearest' },
  { value: 'nearest (mean)', label: 'methodSelection.methods.nearestMean' },
  { value: 'nearest (max. delta)', label: 'methodSelection.methods.nearestMaxDelta' }
];

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/** Result of field validation */
export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates a numeric field and returns user-friendly error message
 * @param value - The value to validate
 * @param fieldKey - The key of the field being validated
 * @param t - Translation function for localization
 * @returns Validation result with error message if invalid
 */
export const validateNumericField = (
  value: string, 
  fieldKey: keyof typeof VALIDATION_RULES,
  t?: (key: string, options?: Record<string, unknown>) => string
): ValidationResult => {
  const rule = VALIDATION_RULES[fieldKey];
  
  // Empty values are allowed if not required
  if (!value.trim()) {
    if (rule.required) {
      return {
        isValid: false,
        errorMessage: t 
          ? t('validation.required', { field: rule.fieldName })
          : `${rule.fieldName} je obavezno polje`
      };
    }
    return { isValid: true };
  }
  
  // Check if it's a valid number
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) {
    return {
      isValid: false,
      errorMessage: t 
        ? t('validation.mustBeNumber', { field: rule.fieldName })
        : `${rule.fieldName} mora biti broj`
    };
  }
  
  // Check minimum value
  if (numericValue < rule.min) {
    return {
      isValid: false,
      errorMessage: t 
        ? t('validation.tooSmall', { field: rule.fieldName, min: rule.min, unit: rule.unit || '' })
        : `${rule.fieldName} ne može biti manje od ${rule.min}${rule.unit ? ' ' + rule.unit : ''}`
    };
  }
  
  // Check maximum value
  if (numericValue > rule.max) {
    return {
      isValid: false,
      errorMessage: t 
        ? t('validation.tooLarge', { field: rule.fieldName, max: rule.max, unit: rule.unit || '' })
        : `${rule.fieldName} ne može biti veće od ${rule.max}${rule.unit ? ' ' + rule.unit : ''}`
    };
  }
  
  return { isValid: true };
};

/**
 * Validates all parameters and returns comprehensive error information
 * @param parameters - The parameters to validate
 * @param t - Translation function for localization
 * @returns Object with validation results for each field
 */
export const validateAllParameters = (
  parameters: AdjustmentsParameters,
  t?: (key: string, options?: Record<string, unknown>) => string
): Record<keyof ParameterValidationErrors, ValidationResult> => {
  const results = {} as Record<keyof ParameterValidationErrors, ValidationResult>;
  
  // Validate numeric fields
  results.timeStepSize = validateNumericField(parameters.timeStepSize, 'timeStepSize', t);
  results.offset = validateNumericField(parameters.offset, 'offset', t);
  
  // Validate datetime fields
  if (!parameters.startTime.value.trim()) {
    results.startTime = {
      isValid: false,
      errorMessage: t 
        ? t('validation.required', { field: 'Start Time' })
        : 'Start Time je obavezno polje'
    };
  } else {
    results.startTime = { isValid: true };
  }
  
  if (!parameters.endTime.value.trim()) {
    results.endTime = {
      isValid: false,
      errorMessage: t 
        ? t('validation.required', { field: 'End Time' })
        : 'End Time je obavezno polje'
    };
  } else {
    results.endTime = { isValid: true };
  }
  
  return results;
};

/**
 * Checks if all parameters are valid
 * @param parameters - The parameters to validate
 * @param t - Translation function for localization
 * @returns True if all parameters are valid, false otherwise
 */
export const areAllParametersValid = (
  parameters: AdjustmentsParameters,
  t?: (key: string, options?: Record<string, unknown>) => string
): boolean => {
  const results = validateAllParameters(parameters, t);
  return Object.values(results).every(result => result.isValid);
};

/**
 * Helper to transform InfoType data to AnalysisTable format
 */
export const transformToAnalysisFormat = (data: InfoType) => {
  return {
    fileName: data["Name der Datei"],
    mesurmentName: data["Name der Messreihe"],
    startTime: data["Startzeit (UTC)"],
    endTime: data["Endzeit (UTC)"],
    timeStepSize: data["Zeitschrittweite [min]"],
    offset: data["Offset [min]"],
    dataPoints: data["Anzahl der Datenpunkte"],
    numericDataPoints: data["Anzahl der numerischen Datenpunkte"],
    numericPercentage: Number(data["Anteil an numerischen Datenpunkten"]),
  };
};

/**
 * Formats UTC timestamp from milliseconds to string format
 */
export const formatUTCTimestamp = (timestamp: string): string => {
  const date = new Date(parseInt(timestamp));
  return date.toISOString().replace("T", " ").replace(".000Z", "");
};
